import { useRef } from 'react';
import { Shell } from '../components';

const sevenApp = 'https://www.printing.ne.jp/support/mobile/appli_netprint_k.html';
const sevenGuide = 'https://www.printing.ne.jp/support/mobile/print_manual.html';
const networkTop = 'https://networkprint.ne.jp/sharp_netprint/ja/top.aspx';
const networkGuide = 'https://networkprint.ne.jp/info/howto/top.html';

export function PrintPage() {
  const missingAppAnswer = useRef<HTMLDetailsElement>(null);
  return (
    <Shell>
      <title>コンビニ印刷の手順｜うちのこばこ</title>
      <meta
        name="description"
        content="うちのこばこで保存した表紙をコンビニで印刷する手順。印刷用PDFの共有から、セブン‐イレブン・ファミリーマート・ローソンでの受け取りまで案内します。"
      />
      <main className="print-guide-page">
        <header className="print-guide-heading">
          <p className="support-label">うちのこばこの使い方</p>
          <h1>コンビニ印刷の手順</h1>
          <p>わが子が主役の表紙を、紙でも残そう。</p>
        </header>

        <aside className="print-guide-recommendation" aria-label="はじめての印刷におすすめの設定">
          <span>はじめてなら、この設定で</span>
          <strong>A4・カラー・片面</strong>
          <p>表紙の「印刷用PDF」を使います。印刷料金はお店で別途かかります。</p>
        </aside>

        <section className="print-guide-section" aria-labelledby="prepare-title">
          <h2 id="prepare-title">
            <span className="print-guide-number" aria-hidden="true">
              1
            </span>
            アプリで表紙を開く
          </h2>
          <ol className="print-guide-steps">
            <li>
              「表紙」タブの<strong>「保存した表紙」</strong>から、印刷したい表紙を開きます。
            </li>
            <li>
              <strong>「印刷用PDFを共有する」</strong>を押します。
            </li>
            <li>
              共有メニューから、お店に対応した印刷アプリを選びます。PDFをいったん保存し、公式サイトから登録することもできます。
            </li>
          </ol>
          <a
            className="print-guide-text-link"
            href="#missing-app"
            onClick={() => {
              if (missingAppAnswer.current) missingAppAnswer.current.open = true;
            }}
          >
            印刷アプリが見つからないときは？
          </a>
        </section>

        <section className="print-guide-section" aria-labelledby="store-title">
          <h2 id="store-title">
            <span className="print-guide-number" aria-hidden="true">
              2
            </span>
            印刷するお店を選ぶ
          </h2>
          <nav className="print-guide-store-links" aria-label="お店別の印刷手順">
            <a href="#seven-eleven">
              セブン‐イレブン <span aria-hidden="true">↓</span>
            </a>
            <a href="#familymart-lawson">
              ファミリーマート・ローソン <span aria-hidden="true">↓</span>
            </a>
          </nav>

          <div className="print-guide-stores">
            <article className="print-guide-store" id="seven-eleven" aria-labelledby="seven-title">
              <p className="print-guide-service">使うサービス：かんたんnetprint</p>
              <h3 id="seven-title">セブン‐イレブン</h3>
              <p>「かんたんnetprint」アプリは会員登録なしで使えます。</p>
              <ol className="print-guide-steps">
                <li>
                  共有先で「かんたんnetprint」を選び、表紙のPDFを登録します。用紙は
                  <strong>A4</strong>、カラーモードは<strong>カラー</strong>に。
                </li>
                <li>
                  登録が完了したら、<strong>QRコードまたはプリント予約番号</strong>
                  と、印刷できる期限を確認します。
                </li>
                <li>
                  お店のマルチコピー機にQRコードをかざすか、予約番号を入力。仕上がり・片面設定・部数・料金を確認して印刷します。
                </li>
              </ol>
              <div className="print-guide-official-links">
                <a href={sevenApp}>
                  公式アプリ・使い方 <span aria-hidden="true">↗</span>
                </a>
                <a href={sevenGuide}>
                  お店での操作（公式） <span aria-hidden="true">↗</span>
                </a>
              </div>
            </article>

            <article
              className="print-guide-store"
              id="familymart-lawson"
              aria-labelledby="network-title"
            >
              <p className="print-guide-service">使うサービス：ネットワークプリント</p>
              <h3 id="network-title">ファミリーマート・ローソン</h3>
              <p>初めてなら、公式サイトの「会員登録なしでご利用になる方」から登録できます。</p>
              <ol className="print-guide-steps">
                <li>
                  表紙のPDFを保存し、公式サイトでそのファイルを選んで登録します。ネットワークプリントの会員の方は、アプリへの共有も使えます。
                </li>
                <li>
                  登録後に発行される<strong>ユーザー番号</strong>
                  と期限を控えます。QRコードが表示された場合は保存しておきましょう。
                </li>
                <li>
                  お店のマルチコピー機で「ネットワークプリント」を選んでユーザー番号を入力するか、QRコードをかざします。表紙を選び、
                  <strong>A4・カラー・片面</strong>と部数・料金を確認して印刷します。
                </li>
              </ol>
              <div className="print-guide-official-links">
                <a href={networkTop}>
                  公式サイトでPDFを登録 <span aria-hidden="true">↗</span>
                </a>
                <a href={networkGuide}>
                  使い方・料金（公式） <span aria-hidden="true">↗</span>
                </a>
              </div>
            </article>
          </div>
          <p className="print-guide-small">
            一部店舗では利用できません。対応店舗や画面の操作は、各サービスの公式案内をご確認ください。
          </p>
        </section>

        <section className="print-guide-section" aria-labelledby="check-title">
          <h2 id="check-title">
            <span className="print-guide-number" aria-hidden="true">
              3
            </span>
            仕上がりを確認して、印刷
          </h2>
          <p>
            料金を支払う前に、お店の画面で<strong>表紙全体が収まっているか</strong>
            確認しましょう。PDFが複数ページある場合は、印刷したいページだけを選びます。
          </p>
          <p>
            プリントした紙と、釣り銭・領収書を忘れずに。おうちに飾ったり、おじいちゃん・おばあちゃんへの贈りものにも。
          </p>
        </section>

        <section className="print-guide-section" aria-labelledby="questions-title">
          <h2 id="questions-title">困ったときは</h2>
          <div className="support-questions">
            <details id="missing-app" ref={missingAppAnswer}>
              <summary>印刷アプリが共有メニューに出てきません</summary>
              <div className="support-answer">
                <p>
                  お店に対応したアプリをインストールしてから、共有メニューを開き直してください。iPhoneではアプリの並びを横にスクロールし、「その他」も確認します。
                </p>
                <p>
                  <strong>PDFを保存してから登録する方法もあります。</strong>
                  iPhoneは共有メニューの「ファイルに保存」を選びます。AndroidはGoogleドライブなど、共有先に表示される保存用アプリへ送り、あとでそのPDFを選んでください。
                </p>
                <p>
                  保存したPDFは、
                  <a href="https://www.printing.ne.jp/support/lite/guide/guide.html">
                    かんたんnetprintのWeb版
                  </a>
                  、または<a href={networkTop}>ネットワークプリントの公式サイト</a>
                  から登録できます。公式サイトの案内に沿って、保存先からファイルを選びます。
                </p>
              </div>
            </details>
            <details>
              <summary>端の文字が切れる・余白ができます</summary>
              <div className="support-answer">
                <p>
                  コピー機には紙の端に印刷できない部分があります。「用紙に合わせる」など、表紙全体が収まる設定を選び、拡大やフチなしの設定で文字が切れていないか確認してください。設定名はサービスによって異なります。
                </p>
                <p>
                  表紙と用紙の縦横比が違う場合は、余白ができます。まずはA4の普通紙で、表紙全体を残す印刷がおすすめです。
                </p>
              </div>
            </details>
            <details>
              <summary>写真用紙に印刷したい・ミュージアムの作品も印刷したい</summary>
              <div className="support-answer">
                <p>
                  表紙は<strong>「画像を送る・保存する」</strong>
                  から画像を保存し、公式サービスの写真プリントで登録します。ミュージアムの作品は、作品を開いて
                  <strong>「作品を共有」</strong>から画像を保存してください。
                </p>
                <p>
                  L判・2L判など、選べる用紙はお店によって異なります。写真用紙は画像ファイルを使い、顔や文字が切れていないか印刷前に確認しましょう。録音した声は紙には入りません。
                </p>
              </div>
            </details>
            <details>
              <summary>料金・印刷期限・予約番号について</summary>
              <div className="support-answer">
                <p>
                  印刷料金はうちのこばこのPremium料金には含まれず、印刷時にお店で支払います。最新の料金は
                  <a href="https://www.printing.ne.jp/support/lite/pricelist_lite.html">
                    セブン‐イレブンの料金表
                  </a>
                  、<a href={networkGuide}>ネットワークプリントの料金案内</a>をご確認ください。
                </p>
                <p>
                  期限が切れたときは、保存した表紙のPDFをもう一度登録してください。予約番号・ユーザー番号は各印刷サービスが発行します。うちのこばこの家族招待コードとは別のものです。
                </p>
                <p>
                  番号やQRコードを知っている人は印刷できるため、印刷をお願いする家族など、必要な相手だけに伝えましょう。
                </p>
              </div>
            </details>
          </div>
        </section>

        <footer className="print-guide-footer">
          <a href="/support">
            解決しないときは、サポートへ <span aria-hidden="true">→</span>
          </a>
          <p>公式案内の確認日：2026年9月12日</p>
        </footer>
      </main>
    </Shell>
  );
}
